import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Flame, PencilLine, Ban, AlertTriangle } from 'lucide-react';
import { BadgeEsito } from './Badge';
import { ControlloOlioDialog } from './ControlloOlioDialog';
import { FriggitriceDialog } from './FriggitriceDialog';
import { AnnullaDialog } from './AnnullaDialog';
import { oliApi, erroreOli, dataIt } from './api';

const AZIONI = { nessuna: '', filtrato: 'Filtrato', cambiato: 'Olio cambiato' };

const CardFriggitrice = ({ f, onControllo, onModifica }) => {
  const s = f.stato || {};
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid={`oli-friggitrice-${f.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{f.nome} {!f.attiva && <span className="text-xs text-gray-500">(non in uso)</span>}</p>
          <p className="text-xs text-gray-500">{[f.capacita_litri && `${f.capacita_litri} l`, f.tipo_olio].filter(Boolean).join(' · ')}</p>
          <p className="text-sm text-gray-700 mt-1">
            Ultimo controllo: {s.ultimo_controllo ? `${dataIt(s.ultimo_controllo)}${s.polari != null ? ` · ${s.polari}%` : ''}` : 'mai'} <BadgeEsito esito={s.esito} />
          </p>
          <p className="text-xs text-gray-500">Ultimo cambio olio: {s.ultimo_cambio ? dataIt(s.ultimo_cambio) : '-'}</p>
          {s.da_cambiare && (
            <p className="text-xs text-red-700 font-semibold mt-1 flex items-center gap-1" data-testid={`oli-friggitrice-da-cambiare-${f.id}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> Olio oltre il limite: cambialo
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onControllo(f)} data-testid={`oli-friggitrice-controllo-${f.id}`}
            className="px-3 h-9 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium">Controllo</button>
          <button onClick={() => onModifica(f)} data-testid={`oli-friggitrice-modifica-${f.id}`} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg self-end">
            <PencilLine className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const RigaControllo = ({ c, nome, responsabile, onAnnulla }) => (
  <div className={`flex items-start justify-between gap-2 py-2 border-b border-gray-100 text-sm ${c.annullato ? 'opacity-60' : ''}`} data-testid={`oli-controllo-${c.id}`}>
    <div className="min-w-0">
      <p className={`text-gray-900 ${c.annullato ? 'line-through' : ''}`}>
        {dataIt(c.data_ora)} {c.data_ora.slice(11, 16)} · {nome}
        {c.polari_percentuale != null ? ` · ${c.polari_percentuale}%` : ` · a vista (${c.visivo?.colore})`}
        {c.temperatura != null && ` · ${c.temperatura}°C`}
      </p>
      <p className="text-xs text-gray-500">
        <BadgeEsito esito={c.esito} /> {AZIONI[c.azione]} · {c.operatore}
        {c.annullato && ` · annullato: ${c.annullamento?.motivo}`}
      </p>
    </div>
    {responsabile && !c.annullato && (
      <button onClick={() => onAnnulla(c)} data-testid={`oli-controllo-annulla-${c.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Annulla">
        <Ban className="h-4 w-4" />
      </button>
    )}
  </div>
);

// Registro delle friggitrici: controlli dell'olio (composti polari o a vista), filtrazioni e cambi
export const RegistroFriggitrici = ({ responsabile, onCambiato }) => {
  const [friggitrici, setFriggitrici] = useState([]);
  const [controlli, setControlli] = useState([]);
  const [controllo, setControllo] = useState(null);
  const [modifica, setModifica] = useState(undefined);
  const [annulla, setAnnulla] = useState(null);

  const carica = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([oliApi.get('/friggitrici'), oliApi.get('/friggitrici/controlli?limit=40')]);
      setFriggitrici(f.friggitrici || []);
      setControlli(c.controlli || []);
    } catch (e) {
      toast.error(erroreOli(e, 'Registro friggitrici non disponibile'));
    }
  }, []);
  useEffect(() => { carica(); }, [carica]);
  const salvato = () => { setControllo(null); setModifica(undefined); carica(); onCambiato(); };
  const nomi = Object.fromEntries(friggitrici.map((f) => [f.id, f.nome]));

  return (
    <div className="space-y-3" data-testid="oli-friggitrici">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900">Olio di frittura</h3>
        <button onClick={() => setModifica(null)} data-testid="oli-friggitrice-nuova"
          className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium">
          <Plus className="h-4 w-4" /> Friggitrice
        </button>
      </div>
      <p className="text-xs text-gray-500">L'ASL controlla l'olio in uso: sopra il 25% di composti polari va cambiato (Circ. Min. Sanità 1/1991). Registra ogni controllo e ogni cambio.</p>
      {!friggitrici.length && (
        <div className="text-center py-8 text-gray-500"><Flame className="h-10 w-10 mx-auto mb-2 text-gray-300" /><p>Aggiungi le tue friggitrici per iniziare</p></div>
      )}
      {friggitrici.map((f) => <CardFriggitrice key={f.id} f={f} onControllo={setControllo} onModifica={setModifica} />)}
      {!!controlli.length && (
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="font-semibold text-gray-900 text-sm mb-1">Ultimi controlli</p>
          {controlli.map((c) => <RigaControllo key={c.id} c={c} nome={nomi[c.friggitrice_id] || '-'} responsabile={responsabile} onAnnulla={setAnnulla} />)}
        </div>
      )}
      <ControlloOlioDialog friggitrice={controllo} onClose={() => setControllo(null)} onSaved={salvato} />
      <FriggitriceDialog open={modifica !== undefined} friggitrice={modifica} onClose={() => setModifica(undefined)} onSaved={salvato} />
      <AnnullaDialog open={!!annulla} titolo="Annulla controllo" descrizione="Il controllo resta nel registro come annullato, con il motivo."
        onClose={() => setAnnulla(null)}
        onConferma={async (motivo) => { await oliApi.post(`/friggitrici/controlli/${annulla.id}/annulla`, { motivo }); toast.success('Controllo annullato'); carica(); onCambiato(); }} />
    </div>
  );
};
